import { createContext, memo, ReactNode, useContext, useMemo } from 'react';
import { Platform, useWindowDimensions } from 'react-native';
import { EdgeInsets, useSafeAreaInsets } from 'react-native-safe-area-context';
import { useIsMobileLayout } from '../../components/styles/responsive';
import { useResponsiveContext } from '../../functions/responsive/ResponsiveContext';
import { PixelScaleFn } from '../../functions/responsive/utils';

export type MainNavStatus = 'expanded' | 'collapsed' | 'hidden';

const kMainNavCollapseBreakpointWidth = 1260;
const kHeaderHeight = 68;
const kMainNavExpandedWidth = 220;
const kMainNavCollapsedWidth = 72;

export type AppLayoutContextProps = {
  rpx: PixelScaleFn;
  safeAreaInsets: EdgeInsets;
  isMobileWeb: boolean;
  isMobileLayout: boolean;
  mainNavWidth: number;
  mainNavStatus: MainNavStatus;
  headerHeight: number;
  mainContentWidth: number;
  mainContentHeight: number;
  vw: number;
  vh: number;
};

export const AppLayoutContext = createContext<AppLayoutContextProps>(
  null as any,
);

export const useAppLayoutContext = () => useContext(AppLayoutContext);

export const AppLayoutProvider = memo(
  ({ children }: { children?: ReactNode }) => {
    const isMobileLayout = useIsMobileLayout();
    const safeAreaInsets = useSafeAreaInsets();
    const { rpx } = useResponsiveContext();
    const { width: vw, height: vh } = useWindowDimensions();
    const headerHeight = isMobileLayout
      ? 0
      : rpx(kHeaderHeight) + safeAreaInsets.top;
    const mainNavStatus: MainNavStatus = isMobileLayout
      ? 'hidden'
      : vw <= kMainNavCollapseBreakpointWidth
      ? 'collapsed'
      : 'expanded';
    const mainNavWidth =
      mainNavStatus === 'hidden'
        ? 0
        : mainNavStatus === 'expanded'
        ? rpx(kMainNavExpandedWidth)
        : rpx(kMainNavCollapsedWidth);
    const isMobileWeb = isMobileLayout && Platform.OS === 'web';
    const mainContentWidth = vw - mainNavWidth;
    const mainContentHeight = vh - headerHeight;
    const value = useMemo<AppLayoutContextProps>(
      () => ({
        rpx,
        safeAreaInsets,
        isMobileLayout,
        isMobileWeb,
        mainNavWidth,
        mainNavStatus,
        headerHeight,
        mainContentWidth,
        mainContentHeight,
        vw,
        vh,
      }),
      [
        headerHeight,
        isMobileLayout,
        isMobileWeb,
        mainContentWidth,
        mainContentHeight,
        mainNavStatus,
        mainNavWidth,
        rpx,
        safeAreaInsets,
        vh,
        vw,
      ],
    );
    return (
      <AppLayoutContext.Provider value={value}>
        {children}
      </AppLayoutContext.Provider>
    );
  },
);
